import { dominantAxisDeg } from "./plan-edits.js";
import type { PlanPoint, PlanRoom, PlanSegment } from "./scan-plan.js";

/**
 * Which way up a floor plan is drawn.
 *
 * A LiDAR scan's axes are wherever the phone was pointing when the session
 * started, so every plan used to come out tilted at some arbitrary angle —
 * "not rotated in some angle, front view should be on x axis" (2026-09-22).
 * This works out the turn that fixes that, in two parts:
 *
 * 1. SQUARE: minus the storeys' main wall direction (`dominantAxisDeg`, the
 *    same axis "Straighten" squares a wall to), within ±45° — the smallest turn
 *    that runs the walls along X and Y.
 * 2. FRONT: whole quarter turns on top, so the front of the building is along
 *    the X axis at the bottom of the drawing. The survey's own choice when
 *    somebody has made one ("Rotate 90°"); otherwise the side the entrance
 *    door is on — a door in a wall on the outside edge of the lowest storey;
 *    otherwise none.
 *
 * Only ever a DRAWING turn. Everything stored against the plan — corrections,
 * dimensions, objects, comments — stays in the scan's own frame; the portal
 * turns the view (`PlanFit.rotationDeg`) and an export turns its copy of the
 * geometry (`turnPlanRoom`, `turnPlanPoint`), so nothing is ever re-saved.
 *
 * Turns are in degrees, positive from +X toward +Y — clockwise on screen,
 * where plan +Y is drawn downward.
 */

export type PlanFrontSide = "bottom" | "right" | "top" | "left";

export interface PlanOrientation {
  /** The whole turn: `squareDeg + 90 × quarterTurns`, within (−180, 180]. */
  readonly turnDeg: number;
  /** The part that squares the walls to the axes, within (−45, 45]. */
  readonly squareDeg: number;
  /** Whole quarter turns on top, 0–3. */
  readonly quarterTurns: number;
  /** Where the quarter turns came from. */
  readonly front: "chosen" | "door" | "none";
}

/** Quarter turns that bring each side of the squared plan to the bottom. */
const QUARTERS_FOR: Readonly<Record<PlanFrontSide, number>> = { bottom: 0, right: 1, top: 2, left: 3 };

/** How far, in metres, a door may sit from the outside edge and still be on it. */
const EDGE_M = 0.6;

function normalised(deg: number): number {
  const d = ((deg % 360) + 360) % 360;
  return d > 180 ? d - 360 : d;
}

/** `p` turned by `deg` about the plan's origin. */
export function turnPlanPoint(p: PlanPoint, deg: number): PlanPoint {
  if (deg === 0) return p;
  const t = (deg * Math.PI) / 180;
  const c = Math.cos(t);
  const s = Math.sin(t);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}

function turnSegment(segment: PlanSegment, deg: number): PlanSegment {
  return { ...segment, start: turnPlanPoint(segment.start, deg), end: turnPlanPoint(segment.end, deg) };
}

/** A room with every point turned by `deg` — for a drawing, never for storing. */
export function turnPlanRoom(room: PlanRoom, deg: number): PlanRoom {
  if (deg === 0) return room;
  return {
    ...room,
    centre: turnPlanPoint(room.centre, deg),
    outlines: room.outlines.map((outline) => outline.map((p) => turnPlanPoint(p, deg))),
    segments: room.segments.map((s) => turnSegment(s, deg)),
  };
}

/**
 * The side of the squared plan its entrance door is on: a door lying along the
 * outside edge of the lowest storey, weighted by width when there are several
 * (a front door and a back door: the wider wins, then the one to the bottom).
 * Undefined when no door is on an outside edge.
 */
export function entranceSide(rooms: readonly PlanRoom[], squareDeg: number): PlanFrontSide | undefined {
  if (rooms.length === 0) return undefined;
  const lowest = Math.min(...rooms.map((r) => r.story));
  const storey = rooms.filter((r) => r.story === lowest).map((r) => turnPlanRoom(r, squareDeg));
  const walls = storey.flatMap((r) => r.segments.filter((s) => s.kind === "wall"));
  if (walls.length === 0) return undefined;
  const xs = walls.flatMap((w) => [w.start.x, w.end.x]);
  const ys = walls.flatMap((w) => [w.start.y, w.end.y]);
  const edge = { left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) };

  const votes: Record<PlanFrontSide, number> = { bottom: 0, right: 0, top: 0, left: 0 };
  const seen = new Set<string>();
  for (const door of storey.flatMap((r) => r.segments.filter((s) => s.kind === "door"))) {
    // A door between two rooms of one split capture is listed in both.
    if (seen.has(door.id)) continue;
    seen.add(door.id);
    const dx = door.end.x - door.start.x;
    const dy = door.end.y - door.start.y;
    const width = Math.hypot(dx, dy);
    if (!(width > 0)) continue;
    const mid = { x: (door.start.x + door.end.x) / 2, y: (door.start.y + door.end.y) / 2 };
    const across = Math.abs(dx) >= Math.abs(dy);
    const candidates: readonly [PlanFrontSide, number][] = across
      ? [
          ["bottom", Math.abs(edge.bottom - mid.y)],
          ["top", Math.abs(mid.y - edge.top)],
        ]
      : [
          ["right", Math.abs(edge.right - mid.x)],
          ["left", Math.abs(mid.x - edge.left)],
        ];
    const [side, distance] = candidates[0]![1] <= candidates[1]![1] ? candidates[0]! : candidates[1]!;
    if (distance <= EDGE_M) votes[side] += width;
  }
  let best: PlanFrontSide | undefined;
  for (const side of ["bottom", "right", "top", "left"] as const) {
    if (votes[side] > 0 && (best === undefined || votes[side] > votes[best] + 1e-9)) best = side;
  }
  return best;
}

/**
 * The turn a plan is drawn at — see the file header. `chosenQuarterTurns` is
 * the survey's saved choice (0–3), or null/undefined to find the front from
 * the entrance door.
 */
export function planOrientation(
  rooms: readonly PlanRoom[],
  chosenQuarterTurns?: number | null,
): PlanOrientation {
  const walls = rooms.flatMap((r) => r.segments.filter((s) => s.kind === "wall"));
  const axis = dominantAxisDeg(walls.map((w) => ({ start: w.start, end: w.end })));
  // The main direction is in [0, 90): turn back by it, or on by the rest of
  // the quarter, whichever is the smaller turn.
  const squareDeg = axis === 0 ? 0 : axis > 45 ? 90 - axis : -axis;

  let quarterTurns = 0;
  let front: PlanOrientation["front"] = "none";
  if (chosenQuarterTurns !== null && chosenQuarterTurns !== undefined) {
    quarterTurns = ((Math.round(chosenQuarterTurns) % 4) + 4) % 4;
    front = "chosen";
  } else {
    const side = entranceSide(rooms, squareDeg);
    if (side !== undefined) {
      quarterTurns = QUARTERS_FOR[side];
      front = "door";
    }
  }
  return { turnDeg: normalised(squareDeg + 90 * quarterTurns), squareDeg, quarterTurns, front };
}
